import { Component, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NzInputNumberModule } from "ng-zorro-antd/input-number";
import { NzRadioModule } from "ng-zorro-antd/radio";
import { NzToolTipModule } from "ng-zorro-antd/tooltip";
import { ScreenshotFrameService, SLIDE_SHAPE_PRESETS } from "../services/screenshot-frame.service";

// The screenshot "Frame" controls (Consistent / Screen, slide shape, custom size), shared by the
// model page's Screenshot submenu and the shot page's Screenshot menu so both look and behave
// the same. Every row is always rendered (disabled rather than hidden) so the block keeps a
// constant size — both menus close on hover-out, so a resize could shift them from under the mouse.
@Component({
  selector: "app-screenshot-frame-settings",
  standalone: true,
  imports: [FormsModule, NzRadioModule, NzInputNumberModule, NzToolTipModule],
  template: `
    <div class="frame-settings" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
      <nz-radio-group [(ngModel)]="frame.mode" nzSize="small" class="frame-buttons">
        <label nz-radio-button nzValue="slide" nz-tooltip
          nzTooltipTitle="Same image on any computer: laid out to the shape below for the current heights and exported at a fixed size, so captures from different machines line up">Consistent</label>
        <label nz-radio-button nzValue="screen" nz-tooltip
          nzTooltipTitle="As it looks on this screen — won't line up with captures from other computers">Screen</label>
      </nz-radio-group>

      <div class="frame-row">
        <span class="frame-label">Shape</span>
        <nz-radio-group nzSize="small" class="frame-buttons"
          [ngModel]="frame.slideShapeId()" (ngModelChange)="frame.selectSlideShape($event)"
          [nzDisabled]="frame.mode() !== 'slide'">
          @for (shape of shapePresets; track shape.id) {
            <label nz-radio-button [nzValue]="shape.id" nz-tooltip [nzTooltipTitle]="shape.label">{{ shape.shortLabel }}</label>
          }
          <label nz-radio-button nzValue="custom">Custom</label>
        </nz-radio-group>
      </div>

      <div class="frame-row">
        <span class="frame-label">Size</span>
        <div class="frame-cm">
          <nz-input-number nzSize="small" [ngModel]="frame.slideShape().widthCm"
            (ngModelChange)="frame.setSlideShapeSize('widthCm', $event)" [nzMin]="1" [nzStep]="0.1"
            [nzDisabled]="frame.mode() !== 'slide' || frame.slideShapeId() !== 'custom'" />
          <span>×</span>
          <nz-input-number nzSize="small" [ngModel]="frame.slideShape().heightCm"
            (ngModelChange)="frame.setSlideShapeSize('heightCm', $event)" [nzMin]="1" [nzStep]="0.1"
            [nzDisabled]="frame.mode() !== 'slide' || frame.slideShapeId() !== 'custom'" />
          <span>cm</span>
        </div>
      </div>
    </div>
  `,
  styles: `
    .frame-settings {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 290px;
      cursor: default;
      line-height: normal;
    }

    .frame-buttons {
      display: flex;
      flex: 1;
      min-width: 0;
      ::ng-deep .ant-radio-button-wrapper {
        flex: 1;
        text-align: center;
        white-space: nowrap;
        padding: 0 6px;
      }
    }

    .frame-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .frame-label {
      width: 40px;
      flex: none;
      color: #888;
      font-size: 12px;
    }

    .frame-cm {
      display: flex;
      align-items: center;
      gap: 6px;
      color: #888;
      nz-input-number {
        width: 80px;
      }
    }
  `,
})
export class ScreenshotFrameSettingsComponent {
  protected frame = inject(ScreenshotFrameService);
  protected readonly shapePresets = SLIDE_SHAPE_PRESETS;
}
